import { ALLOWED_LANGUAGES } from 'src/services/i18n/utils/getI18nSettings'
import { LANGUAGE_VARIANT_BASE } from 'src/services/i18n/utils/languageVariants'

/** Locale used whenever the active language is not one Intl can parse. */
export const DEFAULT_LOCALE = 'en-US'

/**
 * Coerce a widget language into a locale the `Intl` APIs will accept.
 *
 * Not every language the widget ships is a well-formed BCP 47 tag. `en-soc`
 * — the social / sweepstakes copy variant — is a widget-internal label, and
 * `Intl.NumberFormat`, `Intl.DateTimeFormat` and `Number#toLocaleString` all
 * throw `RangeError: Incorrect locale information provided` when handed it.
 * Thrown from render, that escapes to the error boundary and replaces the
 * whole panel with "Something went wrong" (past-races sidebar, 2026-08-27).
 *
 * `ALLOWED_LANGUAGES` already excludes the pseudo-locales (see
 * `EXCLUDED_LANGUAGES` in getI18nSettings), so it is the right allowlist:
 * anything off it falls back to `DEFAULT_LOCALE`.
 *
 * ALWAYS route a language through this before giving it to Intl or a
 * `toLocale*` method. Passing `useGetCurrentLanguage()` straight in is the bug
 * this exists to prevent.
 */
export const safeLocale = (locale?: string | null): string => {
  if (locale && ALLOWED_LANGUAGES.includes(locale)) return locale
  // A copy variant formats as its base language (`es-lotobola` → `es`); `en-soc` keeps the en-US default.
  const base = locale ? LANGUAGE_VARIANT_BASE[locale] : undefined
  return base && base !== 'en' && ALLOWED_LANGUAGES.includes(base) ? base : DEFAULT_LOCALE
}
